import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { PlusCircle, Loader2, Pencil, Play, Trash2, AlertCircle, Download } from 'lucide-react';
import { CreateInterviewModal } from '../interview/CreateInterviewModal';
import { InterviewWorkspace } from '../interview/InterviewWorkspace';
import { VideoModal } from '../interview/VideoModal';
import { isProjectOwner } from '../../lib/permissions';

interface Interview {
  id: string;
  customer: {
    id: string;
    full_name: string;
    email: string;
  };
  comms_platform: {
    name: string;
  };
  comms_platform_id: string;
  created_at: string;
  notes: string;
  custom_field_values: Record<string, any>;
  video_storage_path: string | null;
  video_filename: string | null;
  video_size: number | null;
  video_content_type: string | null;
  video_uploaded_at: string | null;
  interview_type: 'video' | 'text';
}

interface CustomField {
  id: string;
  name: string;
  field_type: string;
  options?: string[];
}

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  customerName: string;
  isDeleting: boolean;
}

function DeleteModal({ isOpen, onClose, onConfirm, customerName, isDeleting }: DeleteModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Delete Interview
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete the interview with {customerName}? This will permanently remove all associated media and insights. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="inline-flex w-full justify-center rounded-lg border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <span className="flex items-center">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </span>
              ) : (
                'Delete'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isDeleting}
              className="mt-3 inline-flex w-full justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface InterviewsListProps {
  projectId: string;
}

export function InterviewsList({ projectId }: InterviewsListProps) {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [editingInterview, setEditingInterview] = useState<Interview | null>(null);
  const [interviewInsights, setInterviewInsights] = useState<any[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    path: string | null;
    customerName: string;
  } | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    interview: Interview | null;
  }>({
    isOpen: false,
    interview: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchInterviews();
    fetchCustomFields();
    checkPermissions();
  }, [projectId]);

  async function checkPermissions() {
    const isOwner = await isProjectOwner(projectId);
    setCanEdit(isOwner);
  }

  async function fetchCustomFields() {
    try {
      const { data, error } = await supabase
        .from('custom_fields')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCustomFields(data || []);
    } catch (error: any) {
      console.error('Error fetching custom fields:', error);
      setError(error.message);
    }
  }

  const refreshInterviews = async () => {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select(`
          id,
          customer:customer_id (
            id,
            full_name,
            email
          ),
          comms_platform:comms_platform_id (
            name
          ),
          comms_platform_id,
          created_at,
          notes,
          custom_field_values,
          video_storage_path,
          video_filename,
          video_size,
          video_content_type,
          video_uploaded_at,
          interview_type
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInterviews(data || []);
    } catch (error: any) {
      setError(error.message);
    }
  };

  async function fetchInterviews() {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select(`
          id,
          customer:customer_id (
            id,
            full_name,
            email
          ),
          comms_platform:comms_platform_id (
            name
          ),
          comms_platform_id,
          created_at,
          notes,
          custom_field_values,
          video_storage_path,
          video_filename,
          video_size,
          video_content_type,
          video_uploaded_at,
          interview_type
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInterviews(data || []);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchInterviewInsights(interviewId: string) {
    try {
      const { data, error } = await supabase
        .from('insights')
        .select('*')
        .eq('interview_id', interviewId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching insights:', error);
      return [];
    }
  }

  const handleEditInterview = async (interview: Interview) => {
    try {
      const insights = await fetchInterviewInsights(interview.id);
      setInterviewInsights(insights);
      setEditingInterview(interview);
    } catch (error) {
      console.error('Error preparing interview for editing:', error);
      setError('Failed to load interview data');
    }
  };

  const handleEditComplete = async (data: any) => {
    if (!editingInterview) return;
    
    try {
      console.log('Updating interview with data:', data);

      const updateData = {
        notes: data.notes,
        custom_field_values: data.customFieldValues,
        video_storage_path: data.videoStoragePath,
        video_filename: data.videoFilename,
        video_size: data.videoSize,
        video_content_type: data.videoContentType,
        video_uploaded_at: data.videoUploadedAt
      };

      console.log('Final update data:', updateData);

      const { data: updatedInterview, error } = await supabase
        .from('interviews')
        .update(updateData)
        .eq('id', editingInterview.id)
        .select('*, customer:customer_id(*), comms_platform:comms_platform_id(*)')
        .single();

      if (error) {
        console.error('Error updating interview:', error);
        throw error;
      }

      console.log('Update response:', updatedInterview);
      
      setEditingInterview(null);
      setInterviewInsights([]);
      await fetchInterviews();
    } catch (error) {
      console.error('Error updating interview:', error);
      setError(error instanceof Error ? error.message : 'An error occurred while updating the interview');
    }
  };

  const handleDeleteInterview = async () => {
    if (!deleteModal.interview) {
      console.error('No interview selected for deletion');
      return;
    }
    
    setIsDeleting(true);
    setError(null);
    
    try {
      const interviewId = deleteModal.interview.id;
      console.log('Starting deletion process for interview:', interviewId);

      if (deleteModal.interview.video_storage_path) {
        console.log('Step 1: Deleting video file...');
        const cleanPath = deleteModal.interview.video_storage_path.replace(/^interview_videos\//, '');
        console.log('Clean storage path:', cleanPath);
        
        const { error: storageError } = await supabase.storage
          .from('interview_videos')
          .remove([cleanPath]);

        if (storageError) {
          console.error('Error deleting video file:', storageError);
          setError('Warning: Video file could not be deleted');
        }
      }

      console.log('Step 2: Deleting interview record...');
      const { data: deleteResult, error: deleteError } = await supabase
        .rpc('delete_interview', {
          interview_id: interviewId,
          project_id: projectId
        });

      if (deleteError) {
        console.error('Error deleting interview:', deleteError);
        throw deleteError;
      }

      if (!deleteResult) {
        throw new Error('Interview deletion failed');
      }

      console.log('Interview deleted successfully');

      console.log('Step 3: Refreshing interviews list...');
      await fetchInterviews();
      
      console.log('Deletion process completed successfully');
      setDeleteModal({ isOpen: false, interview: null });
    } catch (error: any) {
      console.error('Error during deletion process:', error);
      setError(error.message || 'Failed to delete interview');
      
      await fetchInterviews();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const { data: interviewsData, error: interviewsError } = await supabase
        .from('interviews')
        .select(`
          id,
          customer:customer_id (
            full_name,
            email,
            helpful_score,
            friendly_score,
            willing_to_speak_again
          ),
          created_at,
          notes,
          custom_field_values,
          insights:insights (
            id,
            title
          )
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (interviewsError) throw interviewsError;

      const { data: fields, error: fieldsError } = await supabase
        .from('custom_fields')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (fieldsError) throw fieldsError;

      const baseHeaders = [
        'Customer Name',
        'Customer Email',
        'Upload Date',
        'Number of Insights',
        'Insight Titles',
        'Helpful Score',
        'Friendly Score',
        'Willing to Speak Again'
      ];
      const customFieldHeaders = fields?.map(field => field.name) || [];
      const headers = [...baseHeaders, ...customFieldHeaders, 'Notes'];

      const rows = interviewsData?.map(interview => {
        const baseData = [
          interview.customer?.full_name || '',
          interview.customer?.email || '',
          new Date(interview.created_at).toLocaleDateString(),
          interview.insights?.length || 0,
          interview.insights?.map(insight => insight.title).join('; ') || '',
          interview.customer?.helpful_score || '',
          interview.customer?.friendly_score || '',
          interview.customer?.willing_to_speak_again === true ? 'Yes' : 
            interview.customer?.willing_to_speak_again === false ? 'No' : ''
        ];

        const customFieldValues = fields?.map(field => {
          const value = interview.custom_field_values?.[field.id];
          if (Array.isArray(value)) {
            return value.join(', ');
          }
          if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
          }
          return value || '';
        }) || [];

        return [...baseData, ...customFieldValues, interview.notes?.replace(/\n/g, ' ') || ''];
      }) || [];

      const csvContent = [
        headers.join(','),
        ...rows.map(row => 
          row.map(cell => 
            typeof cell === 'string' ? 
              `"${cell.replace(/"/g, '""')}"` : 
              cell
          ).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `interviews_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setError('Failed to download CSV file. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-base font-semibold leading-6 text-brand-teal">Interviews</h2>
          <p className="mt-2 text-sm text-gray-700">
            A list of all interviews conducted for this project
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none space-x-4">
          <button
            type="button"
            onClick={handleDownloadCSV}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Download CSV
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center rounded-lg bg-brand-orange px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Interview
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-brand-teal sm:pl-6">
                      Customer
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-brand-teal">
                      Watch Video
                    </th>
                    {canEdit && (
                      <th className="px-3 py-3.5 text-right text-sm font-semibold text-brand-teal">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {interviews.length === 0 ? (
                    <tr>
                      <td colSpan={canEdit ? 3 : 2} className="py-8 text-center text-sm text-gray-500">
                        No interviews yet. {canEdit && 'Start by adding your first interview!'}
                      </td>
                    </tr>
                  ) : (
                    interviews.map((interview) => (
                      <tr key={interview.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="font-medium text-brand-teal">
                            {interview.customer.full_name}
                          </div>
                          <div className="text-gray-500">{interview.customer.email}</div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {interview.interview_type === 'video' && (
                            <button
                              onClick={() => setSelectedVideo({
                                path: interview.video_storage_path,
                                customerName: interview.customer.full_name
                              })}
                              className="inline-flex items-center text-brand-orange hover:text-brand-orange-light transition-colors"
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Watch Video
                            </button>
                          )}
                        </td>
                        {canEdit && (
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
                            <div className="flex justify-end space-x-3">
                              <button
                                onClick={() => handleEditInterview(interview)}
                                className="inline-flex items-center text-brand-orange hover:text-brand-orange-light transition-colors"
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                Edit
                              </button>
                              <button
                                onClick={() => setDeleteModal({ 
                                  isOpen: true, 
                                  interview: interview 
                                })}
                                className="inline-flex items-center text-red-600 hover:text-red-900 transition-colors"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {canEdit && (
        <CreateInterviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          projectId={projectId}
          customFields={customFields}
          onInterviewCreated={refreshInterviews}
        />
      )}

      {editingInterview && (
        <InterviewWorkspace
          isOpen={true}
          onClose={() => {
            setEditingInterview(null);
            setInterviewInsights([]);
          }}
          projectId={projectId}
          customFields={customFields}
          formData={{
            customerId: editingInterview.customer.id,
            customerName: editingInterview.customer.full_name,
            customerEmail: editingInterview.customer.email,
            commsPlatform: editingInterview.comms_platform_id,
            customFieldValues: editingInterview.custom_field_values || {},
            notes: editingInterview.notes || '',
            helpfulScore: 3,
            friendlyScore: 3,
            willingToSpeakAgain: true,
            interviewId: editingInterview.id,
            interviewType: editingInterview.interview_type,
            videoStoragePath: editingInterview.video_storage_path
          }}
          onSubmit={handleEditComplete}
          isEditing={true}
          initialInsights={interviewInsights}
          onSaveComplete={refreshInterviews}
        />
      )}

      {selectedVideo && (
        <VideoModal
          isOpen={true}
          onClose={() => setSelectedVideo(null)}
          projectId={projectId}
          storagePath={selectedVideo.path}
          customerName={selectedVideo.customerName}
        />
      )}

      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, interview: null })}
        onConfirm={handleDeleteInterview}
        customerName={deleteModal.interview?.customer.full_name || ''}
        isDeleting={isDeleting}
      />
    </div>
  );
}